import { z } from 'zod';
import { BIOME_IDS } from '../data/biomes.js';
import { WORLD_EVENT_KINDS } from '../data/world-events.js';
import { CharacterPortraitSchema } from './party.js';
/**
 * The world events (ADR 0050). Nothing to ask: the server tells every player
 * of the world zone, once a second, how the event stands and how they stand
 * in it, then the whole ranking at the end. A game master starts and stops
 * one through `admin_command`.
 */
/** One line of the ranking: who, their portrait, and their share. */
export const EventStandingSchema = CharacterPortraitSchema.extend({
    rank: z.number().int().min(1),
    name: z.string().min(1),
    level: z.number().int().min(1),
    score: z.number().int().min(0),
    /** Share of every point scored, in whole percent. */
    percent: z.number().int().min(0).max(100),
    eligible: z.boolean(),
    podium: z.boolean(),
    /** Fell during the raid (ADR 0050): the score stands, nothing more is added. */
    fallen: z.boolean().default(false),
});
/** The receiver's own line, even out of the top: where they stand and what is missing. */
export const EventSelfSchema = z.object({
    rank: z.number().int().min(0),
    score: z.number().int().min(0),
    percent: z.number().int().min(0).max(100),
    /** The score that passes the threshold now; it moves with the others'. */
    threshold: z.number().int().min(1),
    eligible: z.boolean(),
    /** The receiver fell during the raid: out of it, their score kept. */
    fallen: z.boolean().default(false),
});
const EventWhereSchema = z.object({
    /** The slot, as `scheduledEvent` numbers it; a game master's event takes a negative one. */
    id: z.number().int(),
    kind: z.enum(WORLD_EVENT_KINDS),
    biome: z.enum(BIOME_IDS),
    /**
     * The village's name, and the centre and radius in tiles of where it
     * happens: the village for an invasion, the giant or the cart where they
     * stand now, with the reach that counts as being there.
     */
    place: z.string().min(1),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    radius: z.number().int().min(1),
});
export const WorldEventStateSchema = EventWhereSchema.extend({
    phase: z.enum(['warning', 'running']),
    /** Until it starts (warning) or until the time runs out (running). */
    remainingMs: z.number().int().min(0),
    wave: z.number().int().min(0),
    waves: z.number().int().min(1),
    /** Raiders still standing. */
    foes: z.number().int().min(0),
    /** The warlord, or the giant, is in the field. */
    warlord: z.boolean(),
    /** The gold cart: its way, the share behind it, whether it rolls, where its sprite is. */
    cart: z
        .object({
        progress: z.number().int().min(0).max(100),
        rolling: z.boolean(),
        /** The cart's mob id, for the client to find its sprite; null before it stands. */
        mobId: z.string().nullable().default(null),
        /** The road sampled every `CONVOY.roadSampleEvery` tiles, start to village. */
        road: z
            .array(z.object({ x: z.number().int().min(0), y: z.number().int().min(0) }))
            .max(400)
            .default([]),
    })
        .nullable()
        .default(null),
    defenders: z.number().int().min(0),
    top: z.array(EventStandingSchema).max(5),
    self: EventSelfSchema.nullable(),
});
/** What the receiver earned, if they passed the threshold. */
export const EventRewardSchema = z.object({
    xp: z.number().int().min(0),
    /** The cart's gold, capped (ADR 0050); on its way to the purse through `EconomyService`. */
    gold: z.number().int().min(0).default(0),
    /** Template ids and counts laid at their feet, for them alone. */
    items: z.array(z.object({ templateId: z.string().min(1), quantity: z.number().int().min(1) })),
});
export const WorldEventEndedSchema = EventWhereSchema.extend({
    /** `cancelled`: nobody stood at the gates when it was due to start. */
    outcome: z.enum(['won', 'lost', 'stopped', 'cancelled']),
    ranking: z.array(EventStandingSchema).max(200),
    self: EventSelfSchema.nullable(),
    reward: EventRewardSchema.nullable(),
});
//# sourceMappingURL=world-events.js.map